const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders(token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function fetchBlogPosts({ token, status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`${BACKEND_URL}/blog/posts${qs}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error('Failed to load posts');
  return res.json();
}

export async function fetchBlogPostBySlug(slug, { token } = {}) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${encodeURIComponent(slug)}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error('Post not found');
  return res.json();
}

export async function fetchBlogPostById(id, token) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/by-id/${id}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error('Post not found');
  return res.json();
}

export async function fetchBlogTopics(token) {
  const res = await fetch(`${BACKEND_URL}/blog/topics`, {
    headers: authHeaders(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to load topics');
  return data;
}

export async function generateBlogPost(token, { topic, direction, sendEmail } = {}) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/generate`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ topic, direction, sendEmail }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Generation failed');
  return data;
}

export async function fetchBlogJob(jobId, token) {
  const res = await fetch(`${BACKEND_URL}/blog/jobs/${jobId}`, {
    headers: authHeaders(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to fetch job');
  return data;
}

export async function updateBlogPost(id, token, body) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Update failed');
  return data;
}

export async function approveBlogPost(id, token) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${id}/approve`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Approve failed');
  return data;
}

export async function rejectBlogPost(id, token) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${id}/reject`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Reject failed');
  return data;
}

export async function deleteBlogPost(id, token) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Delete failed');
  return data;
}

export async function regenerateBlogPost(id, token, { topic, direction, sendEmail } = {}) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${id}/regenerate`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ topic, direction, sendEmail }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Regenerate failed');
  return data;
}

export async function uploadBlogCover(id, token, file) {
  const form = new FormData();
  form.append('cover', file);
  const res = await fetch(`${BACKEND_URL}/blog/posts/${id}/cover`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Cover upload failed');
  return data;
}

export async function voteBlogPost(slug, { vote, voterKey }) {
  const res = await fetch(`${BACKEND_URL}/blog/posts/${encodeURIComponent(slug)}/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vote, voter_key: voterKey }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Vote failed');
  return data;
}

const VOTER_KEY_STORAGE = 'pt_blog_voter_key';

export function getOrCreateVoterKey() {
  try {
    let key = localStorage.getItem(VOTER_KEY_STORAGE);
    if (!key) {
      key =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VOTER_KEY_STORAGE, key);
    }
    return key;
  } catch {
    return `v_${Date.now()}`;
  }
}

export function estimateReadMinutes(payload) {
  const sections = payload?.content?.sections || [];
  let text = payload?.content?.h1 || '';
  sections.forEach((s) => {
    text += ` ${s.heading || ''} ${s.body || ''}`;
    (s.subsections || []).forEach((sub) => {
      text += ` ${sub.heading || ''} ${sub.body || ''}`;
    });
  });
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}
